import React from 'react';
import { Button } from '../ui/button';
import { DynamicTable } from '../common/DynamicTable';
import { Pencil, Tag, Trash2 } from 'lucide-react';

export interface Tag {
  id: string;
  titulo: string;
  cor: string;
  prioridade?: number;
  ativo: boolean;
  empresaId: string;
  empresaNome?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface TagsTableProps {
  tags: Tag[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof Tag, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  onRowClick: (row: Tag) => void;
  onEdit: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
  canEdit: boolean;
  showEmpresaColumn?: boolean;
}

export const TagsTable: React.FC<TagsTableProps> = ({
  tags,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onRowClick,
  onEdit,
  onDelete,
  canEdit,
  showEmpresaColumn = false,
}) => {
  const columns = [
    { 
      id: 'titulo' as Extract<keyof Tag, string>, 
      label: 'Tag', 
      sortable: true,
      format: (value: string, row: Tag) => (
        <div className="flex items-center">
          <div 
            className="w-4 h-4 rounded-full mr-2" 
            style={{ backgroundColor: row.cor }}
          />
          <span>{value}</span>
        </div>
      )
    },
    { 
      id: 'cor' as Extract<keyof Tag, string>, 
      label: 'Cor',
      format: (value: string) => (
        <div className="flex items-center">
          <div 
            className="w-6 h-6 rounded-full border border-gray-300" 
            style={{ backgroundColor: value }}
            title={value}
          />
          <span className="ml-2 text-sm text-gray-500">{value}</span>
        </div>
      )
    },
    { 
      id: 'prioridade' as Extract<keyof Tag, string>, 
      label: 'Prioridade',
      align: 'center' as const,
      format: (value: number) => value || 'Padrão'
    },
    ...(showEmpresaColumn ? [
      { 
        id: 'empresaNome' as Extract<keyof Tag, string>, 
        label: 'Empresa',
        sortable: true
      }
    ] : []),
    { 
      id: 'ativo' as Extract<keyof Tag, string>, 
      label: 'Status',
      align: 'center' as const,
      format: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    { 
      id: 'dataCriacao' as Extract<keyof Tag, string>, 
      label: 'Criado em',
      format: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    {
      id: 'actions' as any, // 'actions' is a special column ID
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: Tag) => (
        <div className="flex justify-center space-x-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
            disabled={!canEdit}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
            disabled={!canEdit}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={tags}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      onRowClick={onRowClick}
      mobileCards
      searchPlaceholder="Pesquisar tags..."
    />
  );
};
